import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDemo } from "@/store/DemoContext";
import { Pitch } from "@/components/Pitch";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import { comparisonEntities } from "@/lib/results";
import { agreement, divergenceRows, slotCost } from "@/lib/lineup";
import { FORMATION_COORDS, SLOT_POS, ENTITY } from "@/data/constants";
import { fmtNumber, fmtTime } from "@/lib/format";
import { Lock, Layers, Columns3, ArrowRight } from "lucide-react";

const COLORS = { you: ENTITY.you.color, coach: ENTITY.coach.color, ai: ENTITY.ai.color };

export default function ComparisonView() {
  const { fixtureId } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const { dataset, state, getState, getFixtureById, set } = useDemo();
  const fixture = getFixtureById(fixtureId);
  React.useEffect(() => { if (fixture) set("activeFixtureId", fixture.id); }, [fixture, set]);

  const [mode, setMode] = useState("overlay");
  const [layers, setLayers] = useState({ you: true, coach: true, ai: true });

  const ready = !!(fixture && fixture.homeSquad);
  const fxState = ready ? getState(fixture) : "OPEN";
  const settled = fxState === "SETTLED";
  const home = ready ? dataset.clubsById[fixture.homeId] : null;
  const squad = useMemo(() => (ready ? fixture.homeSquad : []), [ready, fixture]);
  const squadById = useMemo(() => Object.fromEntries(squad.map((p) => [p.id, p])), [squad]);
  const ent = useMemo(() => (ready ? comparisonEntities(fixture, state.submissions[fixtureId]) : null), [ready, fixture, state.submissions, fixtureId]);
  const rows = useMemo(() => (ent ? divergenceRows(ent.you.assignment, ent.coach.assignment, ent.ai.assignment) : []), [ent]);

  if (!ready) {
    return <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400">Fixture not available.</div>;
  }

  const aiAgree = agreement(ent.you.assignment, ent.ai.assignment);
  const coachAgree = agreement(ent.you.assignment, ent.coach.assignment);

  const lockTime = fmtTime(new Date(new Date(fixture.kickoff).getTime() - 65 * 60000).toISOString(), state.language);
  const surname = (pid) => (pid ? squadById[pid]?.surname : "—");

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-5 space-y-5">
      {/* sealed header */}
      <div className="rounded-2xl border border-neutral-800 bg-neutral-900/60 p-4 flex flex-wrap items-center gap-3" data-testid="sealed-header">
        <div className="w-9 h-9 rounded-lg bg-[#3fd7e5]/15 text-[#3fd7e5] grid place-items-center shrink-0"><Lock size={18} /></div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-display">{t("comparison.sealedTitle")}</div>
          <div className="text-sm text-neutral-300">{t("comparison.sealedLine", { time: lockTime })}</div>
        </div>
        <code className="mono text-[11px] text-[#3fd7e5] bg-neutral-950 border border-neutral-800 rounded px-2 py-1 break-all" data-testid="commit-hash">
          {fixture.commitHash.slice(0, 24)}…
        </code>
      </div>

      {/* mode toggle + legend */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 p-1 rounded-xl bg-neutral-900 border border-neutral-800">
          <ModeBtn active={mode === "overlay"} onClick={() => setMode("overlay")} icon={<Layers size={14} />} label={t("comparison.overlay")} testid="compare-mode-overlay" />
          <ModeBtn active={mode === "side"} onClick={() => setMode("side")} icon={<Columns3 size={14} />} label={t("comparison.sideBySide")} testid="compare-mode-side" />
        </div>
        <div className="flex gap-3">
          {["you", "coach", "ai"].map((k) => (
            <Legend key={k} k={k} label={t(`common.${k}`)}
              toggleable={mode === "overlay"} on={layers[k]}
              onToggle={() => setLayers((l) => ({ ...l, [k]: !l[k] }))} />
          ))}
        </div>
      </div>

      {mode === "overlay" ? (
        <OverlayPitch ent={ent} layers={layers} squadById={squadById} club={home} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <EntityPitch title={t("common.you")} color={COLORS.you} data={ent.you} squadById={squadById} club={home} testid="pitch-you" />
          <EntityPitch title={t("common.coach")} subtitle={home.coachName} color={COLORS.coach} data={ent.coach} squadById={squadById} club={home} testid="pitch-coach" />
          <EntityPitch title={t("common.ai")} color={COLORS.ai} data={ent.ai} squadById={squadById} club={home} testid="pitch-ai" />
        </div>
      )}

      {/* agreement summary */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-neutral-800 bg-neutral-900/50 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-display mb-1">{t("comparison.agreementTitle")}</div>
          <p className="text-sm text-neutral-300" data-testid="agree-ai">{t("comparison.youAndAi", { n: aiAgree })}</p>
          <p className="text-sm text-neutral-300 mt-1" data-testid="agree-coach">{t("comparison.youAndCoach", { n: coachAgree })}</p>
        </div>
      </div>

      {/* divergence table */}
      <div className="rounded-xl border border-neutral-800 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-neutral-800 bg-neutral-900/60 flex items-center justify-between">
          <span className="font-display uppercase text-sm tracking-wide">{t("comparison.divergence")}</span>
          {!settled && <span className="text-[11px] text-amber-400">{t("comparison.costAfter")}</span>}
        </div>
        {!settled && <div className="px-4 py-2 text-xs text-neutral-500 bg-neutral-950">{t("comparison.notRevealed")}</div>}
        <table className="w-full text-sm" data-testid="divergence-table">
          <thead>
            <tr className="text-[11px] uppercase text-neutral-500 border-b border-neutral-800">
              <th className="text-left px-4 py-2 font-display">{t("comparison.slot")}</th>
              <th className="text-left px-2 py-2 font-display" style={{ color: COLORS.you }}>{t("common.you")}</th>
              <th className="text-left px-2 py-2 font-display" style={{ color: COLORS.coach }}>{t("common.coach")}</th>
              <th className="text-left px-2 py-2 font-display" style={{ color: COLORS.ai }}>{t("common.ai")}</th>
              <th className="text-right px-4 py-2 font-display">{t("comparison.cost")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const cost = settled ? slotCost(r.slot, r.you, squad, fixture.optimalEGD, ent.optimal.assignment) : null;
              return (
                <tr key={r.slot} className="border-b border-neutral-900 last:border-0">
                  <td className="px-4 py-2 font-display font-bold text-neutral-300">{r.slot}</td>
                  <td className="px-2 py-2 text-neutral-300">{surname(r.you)}</td>
                  <td className="px-2 py-2 text-neutral-300">{surname(r.coach)}</td>
                  <td className="px-2 py-2 text-neutral-300">{surname(r.ai)}</td>
                  <td className="px-4 py-2 text-right tabular font-display">
                    {settled ? (cost != null && cost > 0 ? <span className="text-red-400">−{fmtNumber(cost, state.language, 2)}</span> : <span className="text-neutral-600">0{state.language === "en" ? "." : ","}00</span>)
                      : <span className="text-neutral-700">{t("comparison.costHidden")}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {settled && (
        <button data-testid="to-result-btn" onClick={() => nav(`/result/${fixtureId}`)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#b6ff2e] text-black font-display uppercase tracking-wide hover:bg-[#a2e626]">
          {t("home.viewResult")} <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
}

function ModeBtn({ active, onClick, icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        active ? "bg-[#b6ff2e] text-black" : "text-neutral-400 hover:text-neutral-100"
      }`}>{icon} {label}</button>
  );
}

function Legend({ k, label, toggleable, on, onToggle }) {
  return (
    <button disabled={!toggleable} onClick={onToggle} data-testid={`legend-${k}`}
      className={`flex items-center gap-1.5 text-xs ${toggleable ? "cursor-pointer" : "cursor-default"} ${on ? "opacity-100" : "opacity-35"}`}>
      <span className="w-3 h-3 rounded-full" style={{ background: COLORS[k] }} /> {label}
    </button>
  );
}

function EntityPitch({ title, subtitle, color, data, squadById, club, testid }) {
  const coords = FORMATION_COORDS[data.formation];
  return (
    <div className="rounded-xl border border-neutral-800 p-2" data-testid={testid}>
      <div className="flex items-center justify-between px-1 mb-2">
        <span className="font-display uppercase text-sm tracking-wide" style={{ color }}>{title}</span>
        <span className="text-[10px] text-neutral-500 font-display">{data.formation}</span>
      </div>
      {subtitle && <div className="px-1 text-[10px] text-neutral-500 -mt-1 mb-1">{subtitle}</div>}
      <Pitch>
        {Object.entries(data.assignment).map(([slot, pid]) => {
          const c = coords[slot] || SLOT_POS[slot];
          const p = squadById[pid];
          return (
            <div key={slot} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${c.x}%`, top: `${c.y}%` }}>
              <div className="rounded-full grid place-items-center" style={{ width: 26, height: 26, background: color, color: "#0a0a0a", fontSize: 10, fontWeight: 700 }}>{p?.number}</div>
              <span className="text-[8px] text-neutral-200 bg-neutral-950/70 px-1 rounded mt-0.5 whitespace-nowrap">{p?.surname}</span>
            </div>
          );
        })}
      </Pitch>
    </div>
  );
}

function OverlayPitch({ ent, layers, squadById, club }) {
  // union of slots across the three
  const slots = new Set();
  ["you", "coach", "ai"].forEach((k) => Object.keys(ent[k].assignment).forEach((s) => slots.add(s)));
  return (
    <div className="max-w-md mx-auto w-full" data-testid="overlay-pitch">
      <Pitch>
        {[...slots].map((slot) => {
          const y = ent.you.assignment[slot], c = ent.coach.assignment[slot], a = ent.ai.assignment[slot];
          const agree = y && y === c && c === a;
          const co = SLOT_POS[slot];
          const chips = [];
          if (layers.you && y) chips.push({ k: "you", pid: y });
          if (layers.coach && c) chips.push({ k: "coach", pid: c });
          if (layers.ai && a) chips.push({ k: "ai", pid: a });
          if (chips.length === 0) return null;
          return (
            <div key={slot} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5" style={{ left: `${co.x}%`, top: `${co.y}%` }}>
              {agree ? (
                <div className="rounded-full grid place-items-center bg-neutral-500 text-black" style={{ width: 30, height: 30, fontSize: 11, fontWeight: 700 }}>
                  {squadById[y]?.number}
                </div>
              ) : (
                <div className="flex -space-x-1">
                  {chips.map((ch) => (
                    <div key={ch.k} title={`${ch.k}: ${squadById[ch.pid]?.surname}`}
                      className="rounded-full grid place-items-center border border-neutral-950" style={{ width: 24, height: 24, background: COLORS[ch.k], color: "#0a0a0a", fontSize: 9, fontWeight: 700 }}>
                      {squadById[ch.pid]?.number}
                    </div>
                  ))}
                </div>
              )}
              <span className="text-[8px] font-display text-neutral-400">{slot}</span>
            </div>
          );
        })}
      </Pitch>
    </div>
  );
}
